import { useCallback, useContext } from "react";

import {
  HamburgerCloseIcon,
  HeaderContainer,
  Icon_ShaprLogoTextSmall,
  InfoBar,
  Link,
  Menu,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { useParams } from "react-router-dom";

import { DBAvailableContext } from "../../contexts/DBAvailableProvider";
import useNavigation from "../../hooks/navigation";
import useUrlHelper from "../../hooks/urlHelper";
import useAccount from "../../hooks/useAccount";
import { useResendVerificationEmail } from "../../hooks/useAuth";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { ElementClickAction, ElementClickLocation } from "../../lib/TrackingAdapter";
import { SearchParam } from "../../types";
import { AccountButton } from "../Headers/components/AccountButton/AccountButton";

import style from "./Header.module.css";

const ResendEmailVerification: React.FC = () => {
  const { t, m } = useTranslation();
  const tracker = useTracker();
  const {
    mutate: mutateResendVerificationEmail,
    isSuccess: isResendVerificationEmailSuccess,
    isPending: isResendVerificationEmailPending,
  } = useResendVerificationEmail();

  const resendVerificationEmail = useCallback(() => {
    tracker.trackRequestVerificationEmailClicked({ location: "top_menu" });
    mutateResendVerificationEmail();
  }, [mutateResendVerificationEmail, tracker]);

  return (
    <div>
      {isResendVerificationEmailSuccess || isResendVerificationEmailPending ? (
        <span>{t(m.HeaderVerification.VerificationEmailSent)}</span>
      ) : (
        <Link onClick={resendVerificationEmail}>
          {t(m.HeaderVerification.ResendVerificationEmail)}
        </Link>
      )}
    </div>
  );
};

interface HeaderProps {
  className?: string;
  isLogoutPending?: boolean;
  logout: () => void;
  newZendeskTicket: () => void;
  listZendeskTickets: () => void;
  sidebarOpen?: boolean;
  setSidebarOpen?: (value: boolean) => void;
}

const Header = (props: HeaderProps) => {
  const { t, m } = useTranslation();
  const navigation = useNavigation();
  const tracker = useTracker();
  const { isDBDown } = useContext(DBAvailableContext);

  const { teamID } = useParams();

  const { isInApp, isFromApp } = useUrlHelper();

  const { data } = useAccount();
  const email = data?.email ?? "";
  const emailVerified = data?.emailVerified ?? false;
  const name = data?.name ?? data?.email ?? "";

  const goToProfile = useCallback(() => {
    if (teamID) {
      return navigation.goToTeam({ teamID });
    }

    navigation.goToProfile([
      SearchParam.SubscriptionTier,
      SearchParam.SubscriptionPeriod,
      SearchParam.FromAddSeats,
    ]);
  }, [teamID, navigation]);

  if (isInApp || isFromApp) {
    return null;
  }

  return (
    <HeaderContainer>
      <div className={classNames(style.container)}>
        <div className={style.logoContainer}>
          {props.sidebarOpen !== undefined && (
            <HamburgerCloseIcon
              className={style.hamburger}
              open={props.sidebarOpen}
              onClick={() => props.setSidebarOpen?.(!props.sidebarOpen)}
            />
          )}

          <a onClick={goToProfile}>
            <Icon_ShaprLogoTextSmall title="Shapr3D logo - Go to account" role="img" />
          </a>
        </div>
        {email && (
          <Menu
            activator={<AccountButton name={name} />}
            onOpen={() => {
              tracker.trackButtonClick({
                action: ElementClickAction.OPEN_TOP_RIGHT_MENU,
              });
            }}
          >
            {!emailVerified && (
              <>
                <InfoBar
                  className={style.info}
                  variant="warning"
                  text={
                    <div>
                      {t(m.HeaderVerification.VerifyEmailAddress, {
                        email: <span className={style.emailAddressText}>{email}</span>,
                      })}
                      <br />
                      <ResendEmailVerification />
                    </div>
                  }
                />
                <Menu.Divider />
              </>
            )}
            <Menu.Item
              onClick={() => {
                tracker.trackButtonClick({
                  action: ElementClickAction.GO_TO_ACCOUNT_SETTINGS,
                  location: ElementClickLocation.TOP_RIGHT_MENU,
                });
                goToProfile();
              }}
              closeOnClick
            >
              {t(m.General.AccountSettings)}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Link
              href="https://support.shapr3d.com/hc/en-us"
              target="_blank"
              onClick={() => {
                tracker.trackButtonClick({
                  action: ElementClickAction.GO_TO_HELP_CENTER,
                  location: ElementClickLocation.TOP_RIGHT_MENU,
                });
              }}
              closeOnClick
            >
              {t(m.General.HelpCenter)}
            </Menu.Link>
            <Menu.Link
              href="https://support.shapr3d.com/hc/en-us/articles/12208897524380-Video-tutorials"
              target="_blank"
              onClick={() => {
                tracker.trackButtonClick({
                  action: ElementClickAction.GO_TO_VIDEO_TUTORIALS,
                  location: ElementClickLocation.TOP_RIGHT_MENU,
                });
              }}
              closeOnClick
            >
              {t(m.General.VideoTutorials)}
            </Menu.Link>
            <Menu.Link
              href="https://discourse.shapr3d.com"
              target="_blank"
              onClick={() => {
                tracker.trackButtonClick({
                  action: ElementClickAction.GO_TO_COMMUNITY,
                  location: ElementClickLocation.TOP_RIGHT_MENU,
                });
              }}
              closeOnClick
            >
              {t(m.General.Community)}
            </Menu.Link>
            <Menu.Divider />
            <Menu.Link
              href="https://www.shapr3d.com/download"
              target="_blank"
              onClick={() => {
                tracker.trackButtonClick({
                  action: ElementClickAction.GO_TO_DOWNLOAD,
                  location: ElementClickLocation.TOP_RIGHT_MENU,
                });
              }}
              closeOnClick
            >
              {t(m.General.DownloadShapr3D)}
            </Menu.Link>
            <Menu.Divider />
            <Menu.Item
              disabled={isDBDown || props.isLogoutPending}
              onClick={() => {
                tracker.trackButtonClick({
                  action: ElementClickAction.LOG_OUT,
                  location: ElementClickLocation.TOP_RIGHT_MENU,
                });

                props.logout();
              }}
            >
              {t(m.General.LogOut)}
            </Menu.Item>
          </Menu>
        )}
      </div>
    </HeaderContainer>
  );
};

export default Header;
